import React from 'react';
import { Button, Form, Input, Switch, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProductCategory } from '../productCategoriesApi';
import type { ProductDataGap } from '../productLogistics';
import CategoryTagPicker from './CategoryTagPicker';

interface IdentitySectionProps {
  categories: ProductCategory[];
  disabled: boolean;
  isNew: boolean;
  gaps: ProductDataGap[];
  onBack: () => void;
}

/** Name + category (coloured tags) + codes in one top block; status unobtrusive on the right. */
const IdentitySection: React.FC<IdentitySectionProps> = ({ categories, disabled, isNew, gaps, onBack }) => {
  const { t } = useTranslation(['products']);
  return (
    <section className="product-card-section product-card-identity">
      <div className="product-card-identity__main">
        <Button type="link" className="product-card-back" icon={<ArrowLeftOutlined />} onClick={onBack}>
          {t('products:card.back')}
        </Button>
        <Form.Item
          name="name"
          label={t('products:card.nameLabel')}
          className="product-card-name"
          rules={[{ required: true, whitespace: true, message: t('products:validation.nameRequired') }]}
        >
          <Input placeholder={t('products:card.namePlaceholder')} disabled={disabled} maxLength={255} />
        </Form.Item>
        <Form.Item
          name="productCategoryId"
          label={t('products:card.categoryLabel')}
          rules={isNew ? [{ required: true, message: t('products:validation.categoryRequired') }] : []}
          extra={categories.length === 0
            ? t('products:card.categoryEmpty')
            : (
              <Typography.Link href="/app/products?tab=categories" target="_blank" rel="noopener noreferrer">
                {t('products:card.categoryManage')} →
              </Typography.Link>
            )}
        >
          <CategoryTagPicker categories={categories} disabled={disabled} ariaLabel={t('products:card.categoryLabel')} />
        </Form.Item>
        <div className="product-card-codes">
          <Form.Item name="ean" label={t('products:card.ean')}>
            <Input disabled={disabled} maxLength={64} />
          </Form.Item>
          <Form.Item
            name="cnCode"
            label={t('products:card.cnCode')}
            rules={[{ pattern: /^\d{8}$/, message: t('products:card.cnCodeInvalid') }]}
          >
            <Input disabled={disabled} maxLength={8} inputMode="numeric" />
          </Form.Item>
          <Form.Item name="englishName" label={t('products:card.englishName')}>
            <Input disabled={disabled} maxLength={255} />
          </Form.Item>
        </div>
      </div>
      <div className="product-card-identity__aside">
        <div className="product-card-pills">
          {gaps.length === 0
            ? <span className="product-card-ok">{t('products:card.complete')}</span>
            : gaps.map((gap) => (
              <span key={gap} className="product-card-gap">
                {t('products:card.missingItem', { item: t(`products:card.gaps.${gap}`) })}
              </span>
            ))}
        </div>
        <label className="product-card-switch">
          <Form.Item name="isActive" valuePropName="checked" noStyle>
            <Switch size="small" disabled={disabled} aria-label={t('products:card.active')} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.isActive !== next.isActive}>
            {({ getFieldValue }) => (
              <span>{getFieldValue('isActive') === false ? t('products:card.inactive') : t('products:card.active')}</span>
            )}
          </Form.Item>
        </label>
      </div>
    </section>
  );
};

export default IdentitySection;
